import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { uploadApi } from "@/lib/api-client";
import { getUploadUrl, MAX_ATTACHMENT_SIZE, formatBytes, extractErrorMessage } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, Paperclip, FileText, Download, Trash2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { format } from "date-fns";
import { useConfirm } from "@/providers/ConfirmProvider";

interface AttachmentSectionProps {
    entityId: string;
    entityType: string;
}

export default function AttachmentSection({ entityId, entityType }: AttachmentSectionProps) {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadingFiles, setUploadingFiles] = useState<{
        tempId: string;
        fileName: string;
        fileSize: number;
        previewUrl?: string;
        isImage: boolean;
    }[]>([]);

    const { data: attachments, isLoading } = useQuery({
        queryKey: ['attachments', entityType, entityId],
        queryFn: () => trpcClient.attachments.getByEntity.query({ entityType, entityId }),
        enabled: !!entityId && entityId !== "Đang tải..."
    });

    const createMutation = useMutation({
        mutationFn: (data: { fileName: string; fileUrl: string; fileSize?: number; mimeType?: string }) =>
            trpcClient.attachments.create.mutate({ ...data, entityType, entityId }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
            toast.success("Đính kèm tệp thành công");
        },
        onError: (err) => {
            toast.error(extractErrorMessage(err, "Lỗi khi đính kèm tệp"));
        }
    });

    // Delete mutation with optimistic instant removal
    const deleteMutation = useMutation({
        mutationFn: (id: string) => trpcClient.attachments.delete.mutate({ id }),
        onMutate: async (deletedId: string) => {
            const queryKey = ['attachments', entityType, entityId];
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData(queryKey);
            queryClient.setQueryData(queryKey, (old: any) => {
                if (!Array.isArray(old)) return old;
                return old.filter((item: any) => item.id !== deletedId);
            });
            return { previous, queryKey };
        },
        onError: (err: any, _id: any, context: any) => {
            if (context?.queryKey && context?.previous) {
                queryClient.setQueryData(context.queryKey, context.previous);
            }
            toast.error(extractErrorMessage(err, "Lỗi khi xóa tệp"));
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: ['attachments', entityType, entityId] });
        },
        onSuccess: () => {
            toast.success("Xóa tệp đính kèm thành công");
        }
    });

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Tiền kiểm tra dung lượng file ngay lập tức (0ms)
        if (file.size > MAX_ATTACHMENT_SIZE) {
            toast.error(`Tệp "${file.name}" (${formatBytes(file.size)}) vượt quá giới hạn tối đa cho phép (100MB). Vui lòng nén file hoặc chia sẻ liên kết Google Drive.`);
            if (e.target) e.target.value = '';
            return;
        }

        const tempId = 'temp-' + Date.now();
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
        const previewUrl = isImg ? URL.createObjectURL(file) : undefined;

        // Thêm ngay thẻ upload tạm vào giao diện (0ms)
        setUploadingFiles(prev => [...prev, {
            tempId,
            fileName: file.name,
            fileSize: file.size,
            previewUrl,
            isImage: isImg,
        }]);

        try {
            setIsUploading(true);
            const { fileUrl, fileName } = await uploadApi.uploadFile(file);
            await createMutation.mutateAsync({ fileUrl, fileName, fileSize: file.size, mimeType: file.type });
        } catch (error: any) {
            console.error('File upload error', error);
            toast.error(extractErrorMessage(error, 'Lỗi upload file'));
        } finally {
            setUploadingFiles(prev => prev.filter(f => f.tempId !== tempId));
            setIsUploading(false);
            if (e.target) e.target.value = '';
        }
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirm({
            title: "Xác nhận xóa tệp",
            message: "Bạn có chắc chắn muốn xóa tệp đính kèm này không?"
        });
        if (confirmed) {
            deleteMutation.mutate(id);
        }
    };

    const getPreviewUrl = (fileName: string, fileUrl: string) => {
        const ext = fileName.split('.').pop()?.toLowerCase() || '';
        if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
            return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(getUploadUrl(fileUrl))}`;
        }
        return getUploadUrl(fileUrl);
    };

    const isImage = (fileName: string) => {
        return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
    };

    return (
        <Card className="shadow-sm border-slate-200">
            <CardHeader className="bg-slate-50/50 border-b py-3 px-4 flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <Paperclip className="h-4 w-4 text-slate-500" />
                        Tệp đính kèm
                    </CardTitle>
                    <CardDescription className="text-xs">Quản lý các tài liệu, bản vẽ kỹ thuật liên quan (Tối đa 100MB/tệp)</CardDescription>
                </div>
                <div>
                    <label className="cursor-pointer">
                        <input 
                            type="file" 
                            className="hidden" 
                            onChange={handleFileUpload} 
                            disabled={isUploading} 
                            accept="*/*"
                        />
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex items-center gap-2" 
                            disabled={isUploading}
                            asChild
                        >
                            <span>
                                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                                Tải lên
                            </span>
                        </Button>
                    </label>
                </div>
            </CardHeader>
            <CardContent className="p-4">
                {isLoading ? (
                    <div className="flex justify-center p-8">
                        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                    </div>
                ) : (!attachments || attachments.length === 0) && uploadingFiles.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 text-sm">
                        Chưa có tệp đính kèm nào. Hãy tải lên tệp đầu tiên!
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {uploadingFiles.map((up) => (
                            <div key={up.tempId} className="border border-indigo-200 bg-indigo-50/20 rounded-md p-3 flex flex-col gap-3 group relative transition-colors shadow-sm">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <div className="bg-indigo-100 p-2 rounded-md shrink-0">
                                            {up.isImage && up.previewUrl ? (
                                                <img src={up.previewUrl} alt={up.fileName} className="w-8 h-8 object-cover rounded-sm" />
                                            ) : (
                                                <FileText className="h-6 w-6 text-indigo-500" />
                                            )}
                                        </div>
                                        <div className="truncate">
                                            <p className="text-sm font-medium truncate text-slate-800" title={up.fileName}>{up.fileName}</p>
                                            <p className="text-xs text-indigo-600 flex items-center gap-1 font-medium mt-0.5">
                                                <Loader2 className="w-3 h-3 animate-spin" /> Đang tải lên...
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden mt-auto">
                                    <div className="bg-indigo-600 h-full rounded-full animate-pulse" style={{ width: '75%' }} />
                                </div>
                            </div>
                        ))}
                        {attachments?.map((att: any) => (
                            <div key={att.id} className="border rounded-md p-3 flex flex-col gap-3 group relative hover:border-indigo-200 transition-colors bg-white">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <div className="bg-slate-100 p-2 rounded-md shrink-0">
                                            {isImage(att.fileName) ? (
                                                <img src={getUploadUrl(att.fileUrl)} alt={att.fileName} className="w-8 h-8 object-cover rounded-sm" />
                                            ) : (
                                                <FileText className="h-6 w-6 text-slate-500" />
                                            )}
                                        </div>
                                        <div className="truncate">
                                            <p className="text-sm font-medium truncate" title={att.fileName}>{att.fileName}</p>
                                            <p className="text-xs text-slate-500">
                                                {att.uploader?.fullName || 'Hệ thống'} • {format(new Date(att.createdAt), "dd/MM/yyyy HH:mm")}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 pt-2 border-t mt-auto">
                                    <Button variant="ghost" size="sm" className="h-7 text-xs flex-1" asChild>
                                        <a href={getPreviewUrl(att.fileName, att.fileUrl)} target="_blank" rel="noopener noreferrer">
                                            <ExternalLink className="w-3 h-3 mr-1" /> Mở
                                        </a>
                                    </Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs flex-1" asChild>
                                        <a href={getUploadUrl(att.fileUrl)} download={att.fileName}>
                                            <Download className="w-3 h-3 mr-1" /> Tải về
                                        </a>
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 text-rose-500 hover:bg-rose-50 hover:text-rose-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" 
                                        onClick={() => confirm({ message: 'Bạn có chắc chắn muốn xóa tệp này?' }).then(res => { if (res) deleteMutation.mutate(att.id) })}
                                        title="Xóa tệp"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
